import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/hooks/use-toast';

interface SessionTimeoutConfig {
  timeoutMinutes: number;
  warningMinutes: number;
  enabled: boolean;
}

export function useSessionTimeout(
  config: SessionTimeoutConfig = {
    timeoutMinutes: 30,
    warningMinutes: 5,
    enabled: true
  },
  user: any = null,
  signOutCallback?: () => Promise<void>
) {
  const { toast } = useToast();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const warningRef = useRef<NodeJS.Timeout>();
  const intervalRef = useRef<NodeJS.Timeout>();
  const lastActivityRef = useRef<number>(Date.now());

  const resetTimer = () => {
    if (!config.enabled || !user) return;

    lastActivityRef.current = Date.now();
    setShowWarning(false);
    
    // Clear existing timers
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      setShowWarning(true);
      setTimeLeft(config.warningMinutes * 60);
      
      // Start countdown
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleTimeout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      toast({
        title: "Sesión por expirar",
        description: `Tu sesión expirará en ${config.warningMinutes} minutos por inactividad`,
        variant: "destructive"
      });
    }, (config.timeoutMinutes - config.warningMinutes) * 60 * 1000);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, config.timeoutMinutes * 60 * 1000);
  };

  const handleTimeout = async () => {
    setShowWarning(false);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);

    toast({
      title: "Sesión expirada",
      description: "Tu sesión ha expirado por inactividad. Serás redirigido al login.",
      variant: "destructive"
    });

    await signOutCallback?.();
  };

  const extendSession = () => {
    resetTimer();
    toast({
      title: "Sesión extendida",
      description: "Tu sesión ha sido extendida exitosamente",
    });
  };

  useEffect(() => {
    if (!config.enabled || !user) return;

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const resetTimerHandler = () => {
      const now = Date.now();
      // Only reset if there's been significant time between activities (prevent too frequent resets)
      if (now - lastActivityRef.current > 10000) { // 10 segundos threshold (más sensible)
        resetTimer();
      }
    };

    // Add event listeners for user activity
    events.forEach(event => {
      document.addEventListener(event, resetTimerHandler, true);
    });

    // Initialize timer
    resetTimer();

    return () => {
      // Cleanup
      events.forEach(event => {
        document.removeEventListener(event, resetTimerHandler, true);
      });
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [user, config.enabled]);

  return {
    showWarning,
    timeLeft,
    extendSession,
    resetTimer
  };
}

export default useSessionTimeout;